import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import type { AddToCartApiItem } from "~/features/cart/cart/api/AddToCart";
import { sendDataMultipleQuotes } from "~/features/cart/cart/api/MultipleQuoteToCart";
import { addMessage } from "~/features/toasts/redux/actions";
import type { QuoteDetailsSelectionState } from "~/features/orders/common/components/v2/QuoteTable/useQuoteDetailsSelectionState";
import { openModal } from "~/common/utils/ModalService";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { makeMapQuoteLineToATCAPIItem } from "~/features/orders/quoteList/utils";
import type { QuoteListRow } from "~/features/orders/quoteList/useQuoteListState";
import type { QuoteLineMO } from "~/features/orders/common/components/v2/models";
import { getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import { invalidateMinicart } from "~/app/store/bffApi";

interface SelectedATCItemsSummary {
  selectedLineItems: number;
  selectedQuantity: number;
  totalPrice: PriceData;
}

const selectedATCItemsDefaultValue: SelectedATCItemsSummary = {
  selectedLineItems: 0,
  selectedQuantity: 0,
  totalPrice: { value: 0, currencyIso: "" },
};

function getSelectedATCItems(
  rows: QuoteListRow[],
  selectionPerDocument: Record<string, QuoteDetailsSelectionState>,
): SelectedATCItemsSummary {
  const selectedQuoteLineItems: QuoteLineMO[] = rows.flatMap((row) => {
    const documentSelectionState = selectionPerDocument[row.data.soNumber];
    if (!documentSelectionState?.isAnythingSelected) {
      return [];
    }

    const selectedLineItemMap = documentSelectionState.selectedLineNos ?? {};
    return row.details?.quoteLines.filter((line) => selectedLineItemMap[line.lineNo]) ?? [];
  });

  if (selectedQuoteLineItems.length === 0) {
    return selectedATCItemsDefaultValue;
  }

  const totalPieces = selectedQuoteLineItems.reduce(
    (total, item) => total + item.parts.reduce((sum, part) => sum + part.confirmedQuantity, 0),
    0,
  );

  const currencyIso = selectedQuoteLineItems[0]?.parts[0]?.price?.currencyIso;

  const totalPrice = selectedQuoteLineItems.reduce(
    (total, item) =>
      total +
      item.parts.reduce((sum, part) => {
        const priceData = getTotalPrice(part.price, part.confirmedQuantity);
        if (priceData.currencyIso !== currencyIso) {
          throw new Error("Mismatched currencies in selected items");
        }
        return sum + priceData.value;
      }, 0),
    0,
  );

  return {
    selectedLineItems: selectedQuoteLineItems.length,
    selectedQuantity: totalPieces,
    totalPrice: {
      currencyIso: currencyIso ?? "",
      value: totalPrice,
    },
  };
}

interface UseQuoteAddToCartArgs {
  rows: QuoteListRow[];
  selectionPerDocument: Record<string, QuoteDetailsSelectionState>;
}

export function useQuoteAddToCart({ rows, selectionPerDocument }: UseQuoteAddToCartArgs) {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const [isATCInProgress, setIsATCInProgress] = useState(false);

  const selectedATCItems = useMemo(() => getSelectedATCItems(rows, selectionPerDocument), [rows, selectionPerDocument]);

  const disableCheckoutBtn = !rows.some((row) => {
    const documentState = selectionPerDocument[row.data.soNumber];
    return documentState?.isAnythingSelected ?? false;
  });

  const addQuotesToCart = async () => {
    const mapLineItem = makeMapQuoteLineToATCAPIItem();
    const quotes: { quoteNumber: string; items: AddToCartApiItem[] }[] = [];
    rows
      .filter((row) => {
        const documentSelectionState = selectionPerDocument[row.data.soNumber];
        return documentSelectionState?.isAnythingSelected ?? false;
      })
      .forEach((row) => {
        const documentSelectionState = selectionPerDocument[row.data.soNumber];
        const selectedLineItemMap = documentSelectionState?.selectedLineNos ?? {};
        const items = row.details!.quoteLines.filter((line) => selectedLineItemMap[line.lineNo]).flatMap(mapLineItem);
        quotes.push({ quoteNumber: row.data.soNumber, items });
      });

    setIsATCInProgress(true);

    if (quotes.length > 0) {
      try {
        const response = await sendDataMultipleQuotes(quotes);
        if (response.ok) {
          reduxDispatch(invalidateMinicart());
          openModal("add-to-cart-dialog", (close) => (
            <CartSuccessModal onClose={close} page="QUOTE_LIST" responseItems={response.responseItems} />
          ));
          //reduxDispatch(addMessage("info", t("page.quoteDetails.addToCart.success")));
        } else {
          reduxDispatch(addMessage("error", t("page.quoteDetails.errorSending")));
        }
      } catch {
        reduxDispatch(addMessage("error", t("page.quoteDetails.errorSending")));
      } finally {
        setIsATCInProgress(false);
      }
    }
  };

  return {
    addQuotesToCart,
    isATCInProgress,
    selectedATCItems,
    disableCheckoutBtn,
  };
}
